import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import {useCallback, useState} from 'react';
import {shallowEqual, useSelector, useStore} from 'react-redux';
import {v4} from 'uuid';
import {flashGreen} from '~/SignUpSheets/animations/flashGreen';
import {MultipleDatesDropdownMenu} from '~/SignUpSheets/Create/routes/Customize/components/MultipleDatesDropdownMenu/MultipleDatesDropdownMenu';
import {AddButtonWithTitle} from '~/SignUpSheets/components/AddButtonWithTitle/AddButtonWithTitle';
import {RemoveActiveSlotModal} from '~/SignUpSheets/components/LeaveModal/LeaveModal';
import {useUpdateSheet} from '~/SignUpSheets/hooks/useUpdateSheet';
import {
  selectBlueprintType,
  selectFieldSchema,
  selectIsAutoSorting,
  selectIsDateTimeBlueprint,
} from '~/SignUpSheets/reducers/blueprint/selectors';
import {
  selectErrors,
  selectIsInEditMode,
  selectSheetId,
  selectSignupOptionsAreGrouped,
  selectSignupOptionUuids,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {getTime30MinsLater, isAllDay, isSpecificSignup} from '~/SignUpSheets/utils/misc';
import {duplicateSlot} from '~/SignUpSheets/utils/schema';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {Elements} from './Elements/Elements';

import './Builder.sass';

export const Builder = ({
  groupId = null,
  trackChangeUpstream,
  onToggleSignupOption,
  openSignupOptions = new Set(),
}) => {
  const {
    updateSheetDetails,
    deleteSignupOption,
    duplicateSignupOption,
    moveSignupOptionUp,
    moveSignupOptionDown,
    copySlotsToEmptyDates,
    addAnotherSignupOption,
  } = useUpdateSheet();
  const [itemsAnimation] = useAutoAnimate({
    duration: 350,
    easing: 'ease-in-out',
  });
  const sheetId = useSelector(selectSheetId);
  const signupOptionUuids = useSelector(selectSignupOptionUuids(groupId), shallowEqual);
  const store = useStore();
  const fieldSchema = useSelector(selectFieldSchema, shallowEqual);
  const isAutoSorting = useSelector(selectIsAutoSorting);
  const errors = useSelector(selectErrors, shallowEqual);
  const isInEditMode = useSelector(selectIsInEditMode);
  const isDesktop = useIsLargishAndUp();
  const [removeCallback, setRemoveCallback] = useState(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onEdit = useCallback(
    (uuid, payload, pos = -1, saveChanges = true) => {
      // Track change upstream so page knows which group(s) to keep open

      const storeSignupOptions = store.getState().sheet.signup_options;
      const options = [...storeSignupOptions];
      const changedFields = [];
      const index = options.findIndex((i) => i.uuid === uuid);

      if (index >= 0) {
        const item = {...options[index]};
        Object.entries(payload).forEach(([key, val]) => {
          if (key === 'slots') {
            // Make sure we're only dealing with slots that are in the edit payload (or new slots), since
            // the payload should always contain all the latest slots in the signup option
            // NOTE: This also takes care of any deleted slots
            const slots = [...(item.slots ?? [])].filter((s) =>
              payload.slots.some(({slot_id: slotId}) => s.slot_id === slotId)
            );

            payload.slots.forEach((slot, i) => {
              if (i >= slots.length) {
                // New slot; duplicate it with a fresh slot ID
                slots.push(duplicateSlot(slot));
                return;
              }
              const idx = item.slots.findIndex((s) => isSpecificSignup(s, slot));
              if (idx < 0) return; // Edge case handler; if this condition is true something is wrong

              const itemSlot = {...item.slots[idx]};
              Object.entries(slot).forEach(([k, v]) => {
                if (itemSlot[k] !== v) {
                  // Slot field has been changed; add to changed fields
                  if (k === 'quantity') itemSlot[k] = v || null;
                  else itemSlot[k] = v;
                  changedFields.push(['signup_options', pos, 'slots', idx, k]);
                }
              });
              if (
                idx < slots.length &&
                slots[idx].slot_id === itemSlot.slot_id &&
                Object.entries(itemSlot).some(([k, v]) => slots[idx][k] !== v)
              ) {
                // This exact slot has changed; save change
                slots[idx] = itemSlot;
              }
            });
            if (!isEqual(item.slots, slots)) {
              // Slots array has been changed in some way; re-set it
              item.slots = [...slots];
            }

            const lastSlot = slots[slots.length - 1];

            const allDayIsOff =
              payload.all_day === false || (!('all_day' in payload) && item.all_day === false);

            if (allDayIsOff) {
              if (isAllDay(lastSlot.start_time, lastSlot.end_time)) {
                // If toggling times on when times are 12:00 AM - 11:59 PM, set times to 30 min intervals from first time slot
                let previousItem = null;
                item.slots = slots.map((s) => {
                  const mutableSlot = {...s};
                  if (previousItem) {
                    mutableSlot.start_time = getTime30MinsLater(previousItem.start_time);
                    mutableSlot.end_time = getTime30MinsLater(previousItem.end_time);
                  }
                  previousItem = mutableSlot;
                  return mutableSlot;
                });
              } else {
                const lastIncompleteSlot = slots.findIndex((s) => !s.end_time);
                if (lastIncompleteSlot < 0) return;

                for (let i = lastIncompleteSlot; i < slots.length; i++) {
                  if (slots[i].start_time && !slots[i].end_time)
                    slots[i].end_time = getTime30MinsLater(slots[i].start_time);
                  else if (!slots[i].start_time && !slots[i].end_time) {
                    slots[i].start_time =
                      i === 0
                        ? (fieldSchema.slots?.placeholder[0] ?? '10:00 AM')
                        : slots[i - 1].end_time;
                    slots[i].end_time = getTime30MinsLater(slots[i].start_time);
                  }
                }
              }
            }
          } else if (typeof val === 'object') {
            item[key] = {
              // Handle edge case where item[key] was for some reason not an object (shouldn't happen)
              ...(typeof item[key] === 'object' ? item[key] : {}),
              ...val,
            };
            changedFields.push(['signup_options', pos, key]);
          } else {
            if (item[key] === val) return;
            if (key === 'quantity') {
              item.pristine_quantity = false;
              item[key] = val || null;
            } else {
              item[key] = val;
            }
            changedFields.push(['signup_options', pos, key]);
          }
        });

        options.splice(index, 1, item);

        updateSheetDetails({
          // biome-ignore lint/style/useNamingConvention: existing error
          payload: {signup_options: options},
          saveChanges,
          errorsToClear: changedFields,
        });

        if (isAutoSorting && payload?.date && storeSignupOptions.length > 1) {
          flashGreen(`.builder__item-row-${uuid}`);
        }
      }
    },
    [fieldSchema, store, updateSheetDetails, sheetId, errors, groupId]
  );

  const duplicateItem = useCallback(
    (index) => {
      duplicateSignupOption(index, groupId, trackChangeUpstream);
    },
    [duplicateSignupOption, groupId, trackChangeUpstream]
  );

  const deleteItem = useCallback(
    (index, signups = []) => {
      const shouldShowWarning = isInEditMode && signups.length > 0;
      if (shouldShowWarning) {
        setRemoveCallback(() => () => {
          deleteSignupOption(index, groupId, trackChangeUpstream);
          setRemoveCallback(null);
        });
      } else {
        deleteSignupOption(index, groupId, trackChangeUpstream);
      }
    },
    [isInEditMode, deleteSignupOption, groupId, trackChangeUpstream]
  );

  const handleCopySlotsToEmptyDate = useCallback(
    (index) => {
      copySlotsToEmptyDates(index, trackChangeUpstream);
    },
    [copySlotsToEmptyDates, trackChangeUpstream]
  );

  return (
    <>
      <div
        ref={itemsAnimation}
        className={cx('builder__items', {grouped: !!groupId})}
        data-qa-id="builder-items"
      >
        {signupOptionUuids.map((uuid, idx) => (
          <Elements
            key={uuid}
            id={uuid}
            index={idx}
            onEdit={onEdit}
            onDeleteItem={deleteItem}
            onDuplicateItem={duplicateItem}
            onMoveItemUp={moveSignupOptionUp(groupId)}
            onMoveItemDown={moveSignupOptionDown(groupId)}
            onCopySlotsToEmptyDate={handleCopySlotsToEmptyDate}
            grouped={!!groupId}
            expanded={openSignupOptions.has(uuid)}
            onToggle={onToggleSignupOption}
          />
        ))}
        {groupId && (
          <AddButtonWithTitle
            dataQaId="add-another-group-slot"
            onClick={() => addAnotherSignupOption(groupId, trackChangeUpstream)}
            label="Add another slot"
            variant="primary"
            rounded
            wrapperClass={cx({grouped: !!groupId})}
            size="small"
            fullWidth={!isDesktop}
          />
        )}
      </div>
      {removeCallback != null && (
        <RemoveActiveSlotModal cancel={() => setRemoveCallback(null)} remove={removeCallback} />
      )}
    </>
  );
};

export const AddAnother = ({trackChangeUpstream}) => {
  const isDatetimeType = useSelector(selectIsDateTimeBlueprint);
  const signupOptionsAreGrouped = useSelector(selectSignupOptionsAreGrouped);
  const blueprintType = useSelector(selectBlueprintType);
  const [toggleMultipleDateDropdown, setToggleMultipleDateDropdown] = useState(false);
  const {addAnotherSignupOption} = useUpdateSheet();

  const openToggleMultipleDateDropdown = useCallback(() => setToggleMultipleDateDropdown(true), []);
  const closeToggleMultipleDateDropdown = useCallback(
    () => setToggleMultipleDateDropdown(false),
    []
  );

  const addAnother = useCallback(() => {
    const groupId = signupOptionsAreGrouped ? v4() : null;
    addAnotherSignupOption(groupId, trackChangeUpstream);
  }, [addAnotherSignupOption, signupOptionsAreGrouped, trackChangeUpstream]);

  const isItemBlueprint = blueprintType === 'items';
  let label = `Add another ${isItemBlueprint ? 'slot' : 'date'}`;
  if (isItemBlueprint && signupOptionsAreGrouped) label = 'Add time window';

  return (
    <div className="add-new-builder-row__wrapper">
      <AddButtonWithTitle
        dataQaId="add-new-builder-row"
        onClick={isDatetimeType ? openToggleMultipleDateDropdown : addAnother}
        label={label}
        variant="primary"
        fullWidth
        rounded
      />
      <MultipleDatesDropdownMenu
        onClickAddAnother={addAnother}
        open={toggleMultipleDateDropdown}
        onClose={closeToggleMultipleDateDropdown}
      />
    </div>
  );
};
